/**
 * Staged-attachment strip shown above the composer while files upload.
 *
 * Images show a thumbnail, PDFs a labelled tile. Each tile carries its own
 * spinner / retry affordance so one failed upload doesn't block the others.
 */

import { MaterialCommunityIcons } from "@expo/vector-icons";
import {
    ActivityIndicator,
    Image,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";

import type { StagedAttachment } from "@/hooks/useChatAttachments";
import { logRocketRedactProps } from "@/lib/logrocket";
import { colors, fonts, radius, spacing } from "@/lib/theme";

interface Props {
    items: StagedAttachment[];
    onRemove: (localId: string) => void;
    onRetry: (localId: string) => void;
}

export function TicketAttachmentTray({ items, onRemove, onRetry }: Props) {
    if (items.length === 0) {
        return null;
    }

    return (
        <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.tray}
            contentContainerStyle={styles.trayContent}
        >
            {items.map((item) => {
                const isImage = item.mimeType.startsWith("image/");
                return (
                    <View key={item.localId} style={styles.tile}>
                        {isImage ? (
                            // Explicit `cover` — a square crop is the right call
                            // for a small staging thumbnail, and RN Web doesn't
                            // reliably apply the platform default here.
                            <Image
                                {...logRocketRedactProps()}
                                source={{ uri: item.uri }}
                                style={styles.thumb}
                                resizeMode="cover"
                            />
                        ) : (
                            <View style={[styles.thumb, styles.docThumb]}>
                                <MaterialCommunityIcons
                                    name="file-pdf-box"
                                    size={26}
                                    color={colors.error}
                                />
                                <Text style={styles.docLabel} numberOfLines={1}>
                                    {item.filename}
                                </Text>
                            </View>
                        )}

                        {item.status === "uploading" && (
                            <View style={styles.overlay}>
                                <ActivityIndicator size="small" color={colors.surface} />
                            </View>
                        )}

                        {item.status === "failed" && (
                            <TouchableOpacity
                                style={[styles.overlay, styles.overlayFailed]}
                                onPress={() => onRetry(item.localId)}
                                accessibilityLabel={`Retry uploading ${item.filename}`}
                            >
                                <MaterialCommunityIcons
                                    name="refresh"
                                    size={20}
                                    color={colors.surface}
                                />
                                <Text style={styles.retryText}>Retry</Text>
                            </TouchableOpacity>
                        )}

                        <TouchableOpacity
                            style={styles.removeBtn}
                            onPress={() => onRemove(item.localId)}
                            accessibilityLabel={`Remove ${item.filename}`}
                            hitSlop={8}
                        >
                            <MaterialCommunityIcons name="close" size={12} color={colors.surface} />
                        </TouchableOpacity>
                    </View>
                );
            })}
        </ScrollView>
    );
}

const TILE = 64;

const styles = StyleSheet.create({
    tray: { maxHeight: TILE + spacing.md * 2 },
    trayContent: { gap: spacing.sm, paddingBottom: spacing.sm, paddingRight: spacing.sm },
    tile: { width: TILE, height: TILE },
    thumb: {
        width: TILE,
        height: TILE,
        borderRadius: radius.md,
        backgroundColor: colors.gray100
    },
    docThumb: {
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 4,
        borderWidth: 1,
        borderColor: colors.border
    },
    docLabel: {
        fontFamily: fonts.regular,
        fontSize: 8,
        color: colors.textSecondary,
        marginTop: 2
    },
    overlay: {
        ...StyleSheet.absoluteFill,
        borderRadius: radius.md,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(31, 21, 71, 0.55)"
    },
    overlayFailed: { backgroundColor: "rgba(239, 68, 68, 0.75)" },
    retryText: {
        fontFamily: fonts.medium,
        fontSize: 9,
        color: colors.surface,
        marginTop: 2
    },
    removeBtn: {
        position: "absolute",
        top: -6,
        right: -6,
        width: 20,
        height: 20,
        borderRadius: 10,
        backgroundColor: colors.textPrimary,
        alignItems: "center",
        justifyContent: "center"
    }
});
